import { inject, Injectable } from '@angular/core';
import { User } from 'firebase/auth';
import { LoadingController, NavController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AuthService, ProviderSignInResult } from './auth.service';
import { ProfileService } from './profile.service';
import { UserProfile, hasOwnDisplayName } from '../classes/UserProfile';
import { SongDraftIntent, SongDraftService } from './song-draft.service';
import { BackendService } from './backend.service';

export enum NavigationIntent {
  BuyPreviewSong = "buy_preview_song"
}

@Injectable({
  providedIn: 'root'
})
export class AuthFlowService {
  private authService = inject(AuthService);
  private profileService = inject(ProfileService);
  private songDraftService = inject(SongDraftService);
  backendService = inject(BackendService);

  private flowRunning = false;

  /**
   * Wohin es nach der Anmeldung gehen soll, wenn es nicht die Startseite ist.
   *
   * Im sessionStorage und nicht im Dienst: der Anbieter-Login laeuft je nach
   * Browser ueber einen Redirect, und dann ist die App zwischendurch neu
   * geladen. Dieselbe Ueberlegung wie bei `login-intent` darueber.
   */
  private static readonly RETURN_ROUTE_KEY = 'melowish_after_login_route';

  static rememberReturnRoute(route: string[]): void {
    try {
      sessionStorage.setItem(AuthFlowService.RETURN_ROUTE_KEY, route.join('/'));
    } catch {
      // Blockierter Storage - dann geht es eben zur Startseite.
    }
  }

  private takeReturnRoute(): string | null {
    try {
      const route = sessionStorage.getItem(AuthFlowService.RETURN_ROUTE_KEY);
      sessionStorage.removeItem(AuthFlowService.RETURN_ROUTE_KEY);
      return route;
    } catch {
      return null;
    }
  }

  /** Laeuft ein Umhaengen, warten beide Wege auf dasselbe Promise. */
  private mergePromise: Promise<void> | null = null;

  constructor(
    private navCtrl: NavController,
    private loadingCtrl: LoadingController,
  ) {}

  /**
   * Wird einmal beim App Start aufgerufen
   */
  init() {
    this.authService.authState$
      .subscribe(user => this.handleAuthState(user));
  }

  /**
   * Login Buttons rufen nur das auf
   */
  async signInGoogle() {
    await this.runProviderSignIn(
      () => this.authService.signInGoogle(),
      'Google'
    );
  }

  async signInApple() {
    await this.runProviderSignIn(
      () => this.authService.signInApple(),
      'Apple'
    );
  }

  /**
   * Gemeinsamer Ablauf fuer Google und Apple.
   *
   * Neu ist der Schritt `settleAnonymousMerge`: kam der Nutzer aus einer
   * anonymen Sitzung und gehoerte die Identitaet schon einem Konto, ist er
   * jetzt an seinem bestehenden Konto angemeldet - und sein anonym erzeugter
   * Song haengt noch am alten. Der muss umgehaengt sein, **bevor** der
   * Login-Flow weiterlaeuft, sonst schaut der Nutzer im schlimmsten Fall auf
   * eine leere Songliste.
   */
  private async runProviderSignIn(
    signIn: () => Promise<ProviderSignInResult>,
    label: string
  ) {
    let loading: HTMLIonLoadingElement | null = null;

    try {
      sessionStorage.setItem('login-intent', '1');

      const result = await signIn();

      // Ab hier wird es fuer den Nutzer still: das Anbieter-Fenster ist zu,
      // aber Umhaengen und Profilabgleich laufen noch, beides ueber Callables.
      // Bei kaltem Backend sind das zwei bis drei Sekunden, in denen die Seite
      // unveraendert dasteht - und die Anmeldung gescheitert aussieht.
      //
      // Bewusst erst jetzt: waehrend des Popups haette eine Anzeige im
      // Hintergrund niemand gesehen.
      loading = await this.loadingCtrl.create({
        message: 'Anmeldung läuft...',
        cssClass: 'customLoading',
      });
      await loading.present();

      // Muss vor afterLogin stehen: der Nutzer soll nicht auf einer leeren
      // Songliste landen, waehrend sein Song noch umgehaengt wird.
      await this.settleAnonymousMerge();

      // WEB FLOW
      if (result.user) {
        await this.afterLogin(result.user);
      }
      // NATIVE FLOW kommt später über authState
    } catch (e: any) {
      console.error(`${label} Login fehlgeschlagen:`, e);
      sessionStorage.removeItem('login-intent');
      await loading?.dismiss();
      loading = null;
      alert(this.mapAuthError(e));
    } finally {
      await loading?.dismiss();
    }
  }

  /**
   * Stellt sicher, dass ein anstehendes Umhaengen abgeschlossen ist.
   *
   * Zwei Wege fuehren hierher und beide koennen zuerst ankommen: der Aufruf,
   * der die Anmeldung ausgeloest hat, und die authState-Reaktion. Wer zuerst
   * kommt, startet die Arbeit und legt das Promise ab; der zweite wartet auf
   * dasselbe. Ohne das koennte der Login-Flow anlaufen, waehrend der Song noch
   * am alten Konto haengt - und der Nutzer schaut im Moment der Conversion auf
   * eine leere Liste.
   *
   * Scheitert bewusst leise: der Nutzer ist angemeldet, und ihn deswegen aus
   * dem Ablauf zu werfen macht es nicht besser. Der Song ist nicht verloren, er
   * haengt am anonymen Konto und laesst sich per Support nachtragen.
   */
  private async settleAnonymousMerge(): Promise<void> {
    const token = this.authService.takePendingMergeToken();

    if (token) {
      this.mergePromise = (async () => {
        try {
          const result = await this.backendService.claimAnonymousAccount(token);
          console.log('Anonymer Besitz umgehaengt:', result.movedByCollection);
        } catch (e) {
          console.error('Umhaengen des anonymen Besitzes fehlgeschlagen', e);
        }
      })();
    }

    if (this.mergePromise) {
      await this.mergePromise;
    }
  }

  /**
   * Wird aufgerufen wenn Firebase Auth State sich ändert
   */
  private async handleAuthState(user: User | null) {
    if (!user || this.flowRunning) return;

    this.flowRunning = true;
    try {
      // Redirect-Weg: das Umhaengen kann sein Ergebnis nicht an den
      // ausloesenden Aufruf zurueckgeben, die App wurde zwischenzeitlich neu
      // geladen. Deshalb liegt das Token am AuthService bereit.
      await this.settleAnonymousMerge();
      await this.afterLogin(user);
    } finally {
      this.flowRunning = false;
    }
  }

  /**
   * Zentraler Login Flow
   */
  private async afterLogin(user: User) {

    const profile = await this.profileService.ensureProfile(user);

    // Anonyme Konten bekommen keinen Namensdialog. Seit 2.1b entsteht so ein
    // Konto still beim Absenden des Formulars - ein Dialog waere genau die
    // Reibung, die dort gerade abgebaut wurde. Gefragt wird bei der
    // Umwandlung in ein echtes Konto.
    // "Gast" zaehlt dabei nicht als Name: seit anonyme Konten den Platzhalter
    // tragen, wuerde eine blosse Pruefung auf "leer" den Dialog verschlucken -
    // der Nutzer hiesse nach der Registrierung fuer immer Gast.
    if (profile && !hasOwnDisplayName(profile.displayName) && !user.isAnonymous) {
      await this.askForDisplayName();
    }

    const loginIntent = sessionStorage.getItem('login-intent');
    if (!loginIntent) return;
    sessionStorage.removeItem('login-intent');

    if(this.songDraftService.hasDraft()) {
      await this.createPreviewSongFromDraft();
      return;
    }

    // Wer sich aus einem Song heraus angemeldet hat, will zurueck zu diesem
    // Song - nicht auf die Startseite.
    const returnRoute = this.takeReturnRoute();
    await this.navCtrl.navigateRoot(returnRoute ? [returnRoute] : ['home']);
  }

  async createPreviewSongFromDraft() {
    const draftPayload = this.songDraftService.getDraft();
    if(draftPayload) {
      draftPayload.tier = "preview";

      const loading = await this.loadingCtrl.create({
        message: 'Song wird zugeordnet...',
        cssClass: "customLoading"
      });

      loading.present();

      try {
        const resp = await this.backendService.createNewSong(draftPayload);
        console.log('Song request created:', resp);
        loading.dismiss();

        if(this.songDraftService.hasDraft()) {
          this.songDraftService.clearDraft();
        }
        this.navCtrl.navigateRoot(["song-list"]);

      } catch (e: any) {
        console.error('Error creating song from form', e);
        loading.dismiss();
        await this.backendService.processBackendError(e);
      }
    }
  }

  /**
   * Wartet auf Profil Update
   */
  private async askForDisplayName() {

    // dein Dialog
    await this.profileService.askForDisplayName();

    // warten bis Profil wirklich aktualisiert wurde
    await firstValueFrom(
      this.profileService.profile$.pipe(
        filter((p): p is UserProfile => !!p && hasOwnDisplayName(p.displayName))
      )
    );
  }

  mapAuthError(e: any): string {
      const code = e?.code ?? '';
      switch (code) {
        case 'auth/invalid-email':
          return 'Die E-Mail-Adresse ist ungültig.';
        case 'auth/user-disabled':
          return 'Dieses Konto wurde deaktiviert.';
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          return 'E-Mail oder Passwort ist falsch.';
        case 'auth/too-many-requests':
          return 'Zu viele Versuche. Bitte später erneut.';
        case 'auth/popup-closed-by-user':
          return 'Anmeldung abgebrochen.';
        default:
          return 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.';
      }
    }
}
